/**
 * shardedCounter — Distributes Firestore counter writes across multiple shard
 * documents to avoid the ~1 write/sec/doc limit on a single gym document.
 *
 * Shards live at: gyms/{gymId}/counters/shard_{0..9} — the count and the
 * layout come from `@sendly/shared` (`shardedCounter.js`), the server writers
 * and readers address the same documents.
 * Each shard stores numeric fields (e.g. totalAscents, activeRoutes, totalUsers)
 * that are summed when reading.
 */
import { doc, setDoc, getDocs, collection, increment } from "firebase/firestore";
import { db } from "../firebase";
import { randomShardIndex, shardDocId } from "@sendly/shared";
import { isClientOutdated } from "../hooks/useClientCompat";

/**
 * Map a route discipline to its per-discipline sharded counter field name.
 * Unknown/missing values fall back to the boulder counter so older routes
 * (pre-`discipline` field) are still counted somewhere when recalculated.
 */
export function routesCounterFieldFor(discipline) {
  return discipline === "lead" ? "activeRoutesLead" : "activeRoutesBoulder";
}

/**
 * Given an array of route docs, return a map of
 * { [counterField]: count } tallying how many routes fall into each
 * discipline bucket. Used for batch operations (bulk archive / delete) to
 * emit one shard increment per affected discipline rather than one per route.
 */
export function countByDiscipline(routeDocs) {
  const counts = {};
  for (const r of routeDocs) {
    const field = routesCounterFieldFor(r?.discipline);
    counts[field] = (counts[field] || 0) + 1;
  }
  return counts;
}

/**
 * Increment a counter field by distributing the write to a random shard.
 * @param {DocumentReference} gymDocRef - Reference to gyms/{gymId} document
 * @param {string} field - Counter field name (e.g. "totalAscents")
 * @param {number} value - Amount to increment (can be negative)
 */
export async function incrementShard(gymDocRef, field, value) {
  // Below the live compatibility level: a shared counter whose shape the bump
  // rules govern — skip the write rather than risk the old shape.
  if (isClientOutdated()) return;
  const shardRef = doc(gymDocRef, "counters", shardDocId(randomShardIndex()));
  await setDoc(shardRef, { [field]: increment(value) }, { merge: true });
}

/**
 * Read the total value of a sharded counter field by summing all shards.
 * @param {string} gymId - The gym document ID
 * @param {string} field - Counter field name (e.g. "totalAscents")
 * @returns {Promise<number>} The summed total across all shards
 */
export async function readShardedTotal(gymId, field) {
  const countersCol = collection(db, `gyms/${gymId}/counters`);
  const snap = await getDocs(countersCol);
  let total = 0;
  snap.forEach((shard) => {
    const data = shard.data();
    if (typeof data[field] === "number") {
      total += data[field];
    }
  });
  // A total below zero means a decrement raced its matching increment, or a
  // seed/cleanup script over-decremented. Either way, negative counters are
  // nonsense to the UI, so clamp at 0. The underlying shard values remain
  // untouched — a reconciliation job can still see the drift.
  return Math.max(0, total);
}
